/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'jquery',
	'../../../util/DashboardFormatter'
], function(
	$,
	Formatter
) {
	const SINGLE_DATA_ITEM_INDEX = 0;
	const $TICK_TEMPLATE = $('<div class="sliderTickForHorizontal"><div class="tick"></div></div>');
	const $LABEL_TEMPLATE = $('<div class="sliderLabel" title=""></div>');

	class DataPlayerEntryCreator {

		/**
		* @classdesc Creator of the dataplater entry objects. Also adds them to the dom
		* @param {Element} $sliderNode JQuery element to append all entries to
		* @param {Double} barOverflow Total size of the overflow space left on the edges of the bar
		* @param {Object} dataItemAPI API class that is used to work with data items.
		*/
		constructor($sliderNode, barOverflow, dataItemAPI) {
			this.$sliderNode = $sliderNode;
			this.barOverflow = barOverflow;
			this._dataItemAPI = dataItemAPI;
			this.sizeCache = {}; // Cached width and height of a data player entry
		}

		/**
		* @description Create a JQuery element clone from a tick template
		* @returns {Element} JQuery element to represent a dataplayer entry
		*/
		_getNewBaseTick() {
			return $TICK_TEMPLATE.clone();
		}

		/**
		* @description Set the size and width of the cache
		* @param {Element} $tick JQuery tick to use as base size (if not already cached)
		*/
		_updateSizeCache($tick) {
			this.sizeCache.width = this.sizeCache.width ? this.sizeCache.width : $tick[0].clientWidth;
			this.sizeCache.height = this.sizeCache.height ? this.sizeCache.height : $tick[0].clientHeight;
		}

		/**
		* @description Create new Tick Jquery element and append it to the slider.
		* @returns {Element} JQuery element representing the tick
		*/
		_createAndAddBaseTick() {
			// create new tick
			let $tick = this._getNewBaseTick();
			this.$sliderNode.before($tick);
			this._updateSizeCache($tick);
			return $tick;
		}

		/**
		* @description Creator of the dataplater entry objects. Also adds them to the dom
		* @param {Element} $sliderNode JQuery element to append all entries to
		* @param {Double} barOverflow Total size of the overflow space left on the edges of the bar
		* @param {Object} dataItemAPI API class that is used to work with data items.
		*/
		_updateAndDefineTickEntry($tick, barTop, tickSpacing, tickLeftPosition, index = SINGLE_DATA_ITEM_INDEX) {

			const tickTop = barTop - Math.round(this.sizeCache.height / 2);

			// set further attributes
			$tick.attr('data-item-index', index);
			$tick.css('height', this.sizeCache.height + 'px');
			$tick.css('top', tickTop + 'px');
			$tick.css('left', tickLeftPosition + 'px');

			// return tick object
			return {
				top: tickTop,
				left: tickLeftPosition,
				width: this.sizeCache.width,
				height: this.sizeCache.height,
				spacing: tickSpacing
			};
		}

		/**
		* @description Determine the left position of the tick representing the player entry at the specified index
		* @param {Integer} index of the tick of interest
		* @param {Boolean} isSingleDataItem is there only one playerEntry to display
		* @param {Double} tickSpacing spacing between ticks
		* @returns {Integer} left position of the tick for the index specified
		*/
		_getTickLeftPosition(index, isSingleDataItem, tickSpacing) {
			if (isSingleDataItem) {
				return Math.round(this.barOverflow / 2 + tickSpacing - this.sizeCache.width / 2);
			} else {
				return Math.round(this.barOverflow / 2 + tickSpacing * index - this.sizeCache.width / 2);
			}
		}

		/**
		* @description Create and add a tick
		* @param {Integer} index of the tick of interest
		* @param {Double} barTop top position of the bar
		* @param {Double} tickSpacing spacing between ticks
		* @returns {Object} defining the size and position of the tick
		*/
		createTick(index, barTop, tickSpacing) {
			let $tick = this._createAndAddBaseTick();
			const tickLeftPosition = this._getTickLeftPosition(index, false /*isSingleDataItem*/, tickSpacing);
			return this._updateAndDefineTickEntry($tick, barTop, tickSpacing, tickLeftPosition, index);
		}

		/**
		* @description Create and add a tick when there is only one tick to display
		* @param {Double} barTop top position of the bar
		* @param {Double} tickSpacing spacing between ticks
		* @returns {Object} defining the size and position of the tick
		*/
		createSoleTick(barTop, tickSpacing) {
			let $tick = this._createAndAddBaseTick();
			const tickLeftPosition = this._getTickLeftPosition(SINGLE_DATA_ITEM_INDEX, true /*isSingleDataItem*/, tickSpacing);
			return this._updateAndDefineTickEntry($tick, barTop, tickSpacing, tickLeftPosition, SINGLE_DATA_ITEM_INDEX /*index*/);
		}

		/**
		* @description Get the size and position of the specified label JQuery element
		* @param {Element} $label JQuery element of a specified label
		* @param {Boolean} isFirstOrLastLabel Is the first or last label on the slider
		* @param {Double} tickLeft left position for the tick tied to the label
		* @returns {Object} defining the size and position of the label
		*/
		_getLabelSizeAndPosition($label, isFirstOrLastLabel, tickLeft) {
			// calculate label width and truncate the first and last labels as necessary
			let labelWidth = $label[0].clientWidth;
			const actualWidth = labelWidth;
			if (isFirstOrLastLabel) {
				const truncLength = this.barOverflow * 2 - 10; // the 10 is a fudge factor
				if (labelWidth > truncLength) {
					labelWidth = Math.round(truncLength);
					$label.css('width', labelWidth + 'px');
				}
			}

			// calculate the rest of the label info and store in array to be used for positioning
			const labelHeight = $label[0].clientHeight;
			let labelLeft = Math.round(tickLeft - labelWidth / 2);
			let labelRight = labelLeft + labelWidth;

			if (labelLeft < 0) {
				//if the left is off the screen, set it to 0, but add the difference to the right.
				labelRight += (0 - labelLeft);
				labelLeft = 0;
			}
			return {
				left: labelLeft,
				right: labelRight,
				width: labelWidth,
				actualWidth: actualWidth,
				height: labelHeight
			};
		}

		/**
		* @description Create and add a label JQuery element
		* @param {String} value value of the label to create
		* @param {Integer} index of the player PlayerEntry
		* @param {Double} tickLeft left position for the tick tied to the label
		* @param {Double} labelBaseline baseline position of the label
		* @param {Boolean} isFirstOrLastLabel Is the first or last label on the slider
		* @returns {Object} defining the size and position of the label and the label itself
		*/
		_createAndAddLabel(value, index, tickLeft, labelBaseline, isFirstOrLastLabel) {

			// format the text for the label
			const label = Formatter.format(value, this._dataItemAPI.getFormat()) ;

			// create the label object
			let $label = $LABEL_TEMPLATE.clone();
			$label.attr('title', label);
			$label.attr('role', 'option');
			$label.attr('tabindex', '-1');
			$label.text(label);
			this.$sliderNode.before($label);
			$label.attr('data-item-index', index);

			const sizeAndPosition = this._getLabelSizeAndPosition($label, isFirstOrLastLabel, tickLeft);

			return {
				...sizeAndPosition,
				$el: $label,
				labelBaseline: labelBaseline,
				tickLeft: tickLeft
			};
		}

		_getBaseline(barTop) {
			return barTop - this.$sliderNode[0].clientHeight / 2;
		}

		_createLabelAndGetPlayerEntry(dataItem, index, tick, labelBaseline, isFirstOrLastItem) {
			// create new player entry and label
			const label = this._createAndAddLabel(dataItem.label, index, (tick.left + tick.width / 2), labelBaseline, isFirstOrLastItem);
			return {
				index: index,
				value: dataItem,
				tick: tick,
				label: label
			};
		}

		/**
		* @description Create a DataPlayerEntry including tick and label
		* @param {Object} dataItem data item to add to slider
		* @param {Integer} index of the data item
		* @param {Double} barTop top position of the bar
		* @param {Double} tickSpacing space between ticks
		* @param {Boolean} isFirstOrLastLabel Is the first or last label on the slider
		* @returns {Object} PlayerEntry to track
		*/
		createDataPlayerEntry(dataItem, index, barTop, tickSpacing, isFirstOrLastItem) {
			const labelBaseline = this._getBaseline(barTop);

			const tick = this.createTick(index, barTop, tickSpacing);

			return this._createLabelAndGetPlayerEntry(dataItem, index, tick, labelBaseline, isFirstOrLastItem);
		}

		/**
		* @description Create a DataPlayerEntry including tick and label when there is only one data item to render
		* @param {Object} dataItem data item to add to slider
		* @param {Double} barTop top position of the bar
		* @param {Double} tickSpacing space between ticks
		* @returns {Object} PlayerEntry to track
		*/
		createSoleDataPlayerEntry(dataItem, barTop, tickSpacing) {
			const labelBaseline = this._getBaseline(barTop);

			const tick = this.createSoleTick(barTop, tickSpacing);

			return this._createLabelAndGetPlayerEntry(dataItem, SINGLE_DATA_ITEM_INDEX, tick, labelBaseline, true/*isFirstOrLastItem*/);
		}
	}

	return DataPlayerEntryCreator;
});
